import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  INITIAL_MATCH_SEARCH_STATE,
  INITIAL_REFERRAL_CREATION_STATE,
  MatcherContext,
  MatchSearchStatus
} from './MatcherContext';
import { initialMatcherForm } from './MatcherData';
import {
  Answer,
  getAnsweredString,
  MatcherAnswers,
  QuestionId
} from '../flow/types';
import { buildStepQueue } from '../flow/buildStepQueue';
import {
  buildMatcherSearchKey,
  buildMatcherSearchTermsList,
  buildSearchTerms
} from '../flow/buildSearchTerms';
import { createMatchingReferral, searchMatching } from '../api';
import { toMatchedPro } from '../flow/toMatchedPro';
import {
  hasValidVerificationBypass,
  storeMatcherTopMatch
} from '../../../shared/utils';
import { MatchedPro } from '../../../shared/types';
import { getRawText } from '../../../modules/ReferralForm/utils';
import { trackMatchResultReturned } from '../../../shared/analytics';
import {
  MatcherProAgreementText,
  MatcherRamseyAgreementText
} from '../components';
import {
  getMatcherFormName,
  handleMatcherReferralResponse,
  isNoReferral,
  navigateToNoReferralResultsPage
} from '../utils';

const NO_MATCHES_ERROR = "Sorry, we weren't able to find any matching pros.";
const CREATE_REFERRAL_ERROR =
  "Sorry, we weren't able to connect you with any pros. Try\u00A0again.";

class NoMatchesFoundError extends Error {}

function MatcherContextProvider({
  children
}: Readonly<{ children: ReactNode }>) {
  const [matcherForm, setMatcherForm] = useState(initialMatcherForm);
  const [phoneVerificationCountDown, setPhoneVerificationCountDown] =
    useState(0);
  const [startTimer, setStartTimer] = useState(false);
  const [matchSearchState, setMatchSearchState] = useState(
    INITIAL_MATCH_SEARCH_STATE
  );
  const [verificationToken, setVerificationToken] = useState('');
  const [referralCreation, setReferralCreation] = useState(
    INITIAL_REFERRAL_CREATION_STATE
  );
  const { executeRecaptcha } = useGoogleReCaptcha();
  // Mirrors matchSearchState's key/status without the staleness a closure over that state
  // would have inside runMatchSearch (a stable useCallback) - lets the guard below read the
  // latest status synchronously, right after a call it just kicked off, without needing
  // matchSearchState as a dependency.
  const matchSearchRef = useRef<{
    key: string | null;
    status: MatchSearchStatus;
    topMatch: MatchedPro | null;
    promise: Promise<void>;
  }>({
    key: null,
    status: 'idle',
    topMatch: null,
    promise: Promise.resolve()
  });
  // Set synchronously, unlike referralCreation, so two submits in the same tick can't both start.
  const referralInFlight = useRef(false);
  const submittedEmail = useRef<string | null>(null);
  const hasSubmittedReferral = useRef(false);
  // Read from inside the stable callbacks below, which would otherwise close over stale values.
  const latest = useRef({
    matcherAnswers: matcherForm.matcherAnswers,
    verificationToken,
    executeRecaptcha
  });
  useEffect(() => {
    latest.current = {
      matcherAnswers: matcherForm.matcherAnswers,
      verificationToken,
      executeRecaptcha
    };
  }, [matcherForm.matcherAnswers, verificationToken, executeRecaptcha]);

  // A stale failure shouldn't persist once the fan edits anything or retries.
  const clearReferralCreationError = useCallback(() => {
    setReferralCreation((prev) =>
      prev.status === 'error' ? INITIAL_REFERRAL_CREATION_STATE : prev
    );
  }, []);

  const updateMatcherAnswer = useCallback(
    (questionId: QuestionId, answer: Answer) => {
      setMatcherForm((prevForm) => ({
        ...prevForm,
        matcherAnswers: { ...prevForm.matcherAnswers, [questionId]: answer }
      }));
      clearReferralCreationError();
    },
    [clearReferralCreationError]
  );

  function updatePhoneVerificationCountDown(countDown: number) {
    setPhoneVerificationCountDown(countDown);
    setStartTimer(true);
  }

  const updateVerificationToken = useCallback((token: string) => {
    setVerificationToken(token);
  }, []);

  // Timer countdown that sets the number each second.
  useEffect(() => {
    if (!startTimer || phoneVerificationCountDown === 0) {
      return;
    }

    const myInterval = setInterval(() => {
      setPhoneVerificationCountDown((prevCountDown) => {
        if (prevCountDown > 1) {
          return prevCountDown - 1;
        } else {
          clearInterval(myInterval);
          setStartTimer(false);
          return 0;
        }
      });
    }, 1000);

    return () => clearInterval(myInterval);
  }, [startTimer, phoneVerificationCountDown]);

  const stepQueue = useMemo(
    () => buildStepQueue(matcherForm.matcherAnswers),
    [matcherForm.matcherAnswers]
  );

  // Kicked off from MatcherSearchStep on mount rather than waiting for startReferralCreation to
  // need it, so the real search overlaps with the checklist animation instead of adding its own
  // wait later.
  // Keyed on zip+answers (not a simpler "already ran" flag) so a genuinely new zip or answer
  // still starts a fresh search even though a search already ran this session.
  // The returned promise resolves once the search settles (success or error) and never rejects.
  const runMatchSearch = useCallback(
    (postalCode: string, answers: MatcherAnswers): Promise<void> => {
      const searchKey = buildMatcherSearchKey(postalCode, answers);
      const isSameSearch = matchSearchRef.current.key === searchKey;
      if (isSameSearch && matchSearchRef.current.status !== 'error') {
        return matchSearchRef.current.promise;
      }

      matchSearchRef.current = {
        key: searchKey,
        status: 'loading',
        topMatch: null,
        promise: Promise.resolve()
      };
      setMatchSearchState({
        status: 'loading',
        searchKey,
        topMatch: null,
        errorMessage: ''
      });

      // Guarded on both branches below: if a newer search started (a different searchKey is
      // now current) before this one resolves, its result is stale and must not overwrite the
      // newer search's state.
      const searchPromise = searchMatching(
        postalCode,
        buildSearchTerms(answers)
      )
        .then((match) => {
          if (!match) {
            throw new NoMatchesFoundError('No matching pros found');
          }
          if (matchSearchRef.current.key !== searchKey) return;
          const topMatch: MatchedPro = toMatchedPro(match);
          storeMatcherTopMatch(topMatch);
          matchSearchRef.current.status = 'success';
          matchSearchRef.current.topMatch = topMatch;
          setMatchSearchState({
            status: 'success',
            searchKey,
            topMatch,
            errorMessage: ''
          });
          trackMatchResultReturned({
            result_count: match.resultCount,
            search_terms: buildMatcherSearchTermsList(answers),
            pro_office_key: match.officeExternalKey,
            match_score: match.score,
            distance_score: match.distanceScore,
            category_score: match.categoryScore,
            distance_miles: match.distanceMiles,
            runner_up_pro_office_key: match.runnerUpOfficeKey,
            runner_up_match_score: match.runnerUpMatchScore,
            match_result_key: match.matchResultKey,
            rank: match.rank,
            skipped_full_count: match.skippedFullCount,
            tie_break_applied: match.tieBreakApplied,
            tied_account_count: match.tiedAccountCount,
            meeting_score: match.meetingScore,
            runner_up_account_external_key: match.runnerUpAccountExternalKey,
            raw_result_count: match.rawResultCount,
            search_depth: match.searchDepth,
            form_name: getMatcherFormName()
          });
        })
        .catch((e) => {
          console.error(e);
          if (matchSearchRef.current.key !== searchKey) return;
          matchSearchRef.current.status = 'error';
          setMatchSearchState({
            status: 'error',
            searchKey,
            topMatch: null,
            errorMessage: NO_MATCHES_ERROR
          });
          if (e instanceof NoMatchesFoundError) {
            trackMatchResultReturned({
              result_count: 0,
              search_terms: buildMatcherSearchTermsList(answers),
              form_name: getMatcherFormName()
            });
          }
        });
      matchSearchRef.current.promise = searchPromise;
      return searchPromise;
    },
    []
  );

  const startReferralCreation = useCallback(
    async (email: string | null) => {
      try {
        const { matcherAnswers, verificationToken, executeRecaptcha } =
          latest.current;
        const answers: MatcherAnswers = {
          ...matcherAnswers,
          email: email
            ? { status: 'answered', value: email }
            : { status: 'skipped' }
        };
        await runMatchSearch(getAnsweredString(answers.postalCode), answers);
        const { status, topMatch } = matchSearchRef.current;
        if (status !== 'success' || !topMatch) {
          throw new Error('No match available to create a referral for');
        }

        if (isNoReferral()) {
          referralInFlight.current = false;
          navigateToNoReferralResultsPage();
          return;
        }

        const partnershipUrn = topMatch.partnershipURN;
        if (!partnershipUrn) {
          throw new Error('Top match has no partnership URN');
        }
        const { matchResultKey } = topMatch;
        if (!matchResultKey) {
          throw new Error('Top match has no match result key');
        }

        let recaptchaToken = '';
        if (!hasValidVerificationBypass()) {
          if (!executeRecaptcha) {
            throw new Error('Recaptcha not available');
          }
          recaptchaToken = await executeRecaptcha('verification_step');
        }

        const referralResponse = await createMatchingReferral({
          matcherAnswers: answers,
          email,
          partnershipUrn,
          matchResultKey,
          recaptchaToken,
          proAgreement: getRawText(
            <MatcherProAgreementText proName={topMatch.headProName} />
          ),
          ramseyAgreement: getRawText(<MatcherRamseyAgreementText />),
          verificationToken
        });
        // The in-flight flag stays set on success: clearing it would reopen a double-submit
        // window before the navigation below unloads the page.
        await handleMatcherReferralResponse(
          referralResponse,
          answers,
          email,
          partnershipUrn
        );
      } catch (e) {
        console.error('Create referral failure', e);
        referralInFlight.current = false;
        setReferralCreation({
          status: 'error',
          errorMessage: CREATE_REFERRAL_ERROR
        });
      }
    },
    [runMatchSearch]
  );

  const createMatcherReferral = useCallback(
    (email: string | null): boolean => {
      if (referralInFlight.current) return false;
      referralInFlight.current = true;
      hasSubmittedReferral.current = true;
      submittedEmail.current = email;
      setMatcherForm((prevForm) => ({
        ...prevForm,
        matcherAnswers: {
          ...prevForm.matcherAnswers,
          email: email
            ? { status: 'answered', value: email }
            : { status: 'skipped' }
        }
      }));
      setReferralCreation({ status: 'creating', errorMessage: '' });
      void startReferralCreation(email);
      return true;
    },
    [startReferralCreation]
  );

  const retryReferralCreation = useCallback(() => {
    if (hasSubmittedReferral.current) {
      if (referralInFlight.current) return;
      referralInFlight.current = true;
      setReferralCreation({ status: 'creating', errorMessage: '' });
      void startReferralCreation(submittedEmail.current);
      return;
    }
    const { matcherAnswers } = latest.current;
    void runMatchSearch(
      getAnsweredString(matcherAnswers.postalCode),
      matcherAnswers
    );
  }, [runMatchSearch, startReferralCreation]);

  const matcherContextObject = useMemo(() => {
    return {
      matcherAnswers: matcherForm.matcherAnswers,
      updateMatcherAnswer,
      stepQueue,
      phoneVerificationCountDown,
      updatePhoneVerificationCountDown,
      matchSearchState,
      runMatchSearch,
      verificationToken,
      updateVerificationToken,
      referralCreation,
      createMatcherReferral,
      retryReferralCreation,
      clearReferralCreationError
    };
  }, [
    matcherForm,
    updateMatcherAnswer,
    stepQueue,
    phoneVerificationCountDown,
    matchSearchState,
    runMatchSearch,
    verificationToken,
    updateVerificationToken,
    referralCreation,
    createMatcherReferral,
    retryReferralCreation,
    clearReferralCreationError
  ]);

  return (
    <MatcherContext.Provider value={matcherContextObject}>
      {children}
    </MatcherContext.Provider>
  );
}

export { MatcherContextProvider };
